"use client";
import React, { useState } from "react";
import { ProfileSummary } from "../../constants/branchData";
import { BranchSummaryCard } from "./Branches";
import CardGrid from "../../CardGrid";
import { StaffStats, StaffType } from "src/types";
import { useParams } from "next/navigation";
import { useStaffDetails } from "src/app/lib/useStaffDetails";
import Link from "next/link";
import CustomSelect from "./components/ui/form/CustomSelect";
import { ShowMoreText } from "./About1";
import { PropertyType } from "src/types";
import { mockProperties } from "../../constants/mockProperties";
import { VariantCard } from "./components/Listing";
import Skeleton from "react-loading-skeleton";
import { PropertyCardSkeleton } from "../design2/components/Skeletons/Design2PropertyCard";
import Image from "next/image";
import NoSearchCriteria from "../../ui/NoSearchCriteria";

export function areAllValuesZero(
  obj: Record<string, string | number> | StaffStats | undefined
) {
  if (!obj) return;
  return Object.entries(obj).every(([key, value]) => {
    console.log({key})

    // Skip fields that are clearly not numeric counts
    if (typeof value === "string" && isNaN(Number(value))) return true;

    // Check if the value is 0 or "0"
    return value === 0 || value === "0";
  });
}

const Branches1 = () => {
  const search = useParams();
  const { id } = search;
  const toNum = parseInt(id as string);
  const { data: staff, isLoading } = useStaffDetails(toNum);
  return (
    <section className="pt-10">
      <div className="wrapper">
        <div className="grid-container">
          <FirstCol loading={isLoading} {...(staff as StaffType)} />
          {!isLoading && areAllValuesZero(staff?.stats as StaffStats) ? null : (
            <SecondCol loading={isLoading} stats={staff?.stats as StaffStats} />
          )}
        </div>

        {!isLoading &&
        !staff?.about_staff &&
        !staff?.phone &&
        !staff?.email ? null : (
          <div
            style={{
              boxShadow: "0px 2px 10px 0px rgba(33, 33, 33, 0.05)",
            }}
            className="about  text-secondary-main xlg:mt-5 mb-2 bg-white p-4 rounded-xl shadow-xl"
          >
            {!isLoading && !staff?.about_staff ? null : (
              <>
                <h2 className="mb-4  text-xl border-b pb-3 border-b-disabled-main/50  font-semibold">
                  About {isLoading && <Skeleton width={200} />} {staff?.name}
                </h2>
                <div className="items flex-col border-b pb-5 border-b-disabled-main/50 block gap-3">
                  <ShowMoreText
                    loading={isLoading}
                    text={staff?.about_staff || "No information available"}
                  ></ShowMoreText>
                </div>
              </>
            )}

            <div className="icons  flex flex-col py-3 pt-4 gap-2">
              {!isLoading && !staff?.phone ? null : (
                <div className="first items-center flex gap-3">
                  <CallIcon />{" "}
                  {isLoading ? (
                    <Skeleton width={100} />
                  ) : (
                    <Link href={`tel:${staff?.phone}`}> {staff?.phone}</Link>
                  )}
                </div>
              )}
              {!isLoading && !staff?.email ? null : (
                <div className="first items-center flex gap-3">
                  <MessageIcon />{" "}
                  {isLoading ? (
                    <Skeleton width={100} />
                  ) : staff?.email ? (
                    <Link href={`mailto:${staff?.email}`}> {staff?.email}</Link>
                  ) : (
                    "No email available"
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {/* here */}
        {staff?.staffListing && staff?.staffListing.length > 0 && (
          <FourthCol
            loading={isLoading}
            staffListing={staff?.staffListing}
            {...staff!}
          />
        )}
      </div>
    </section>
  );
};
export default Branches1;

interface ExtendedStaffType extends StaffType {
  loading?: boolean;
}

const FirstCol: React.FC<ExtendedStaffType> = ({
  picture,
  name,
  position,
  loading,
  companyImage,
}) => {
  return (
    <div>
      {loading ? (
        <Skeleton height={330} width={"100%"} />
      ) : (
        <Image
          height={400}
          width={400}
          className="rounded-xl  w-full  object-cover max-h-[330px]"
          src={companyImage as string}
          alt="Company Image"
        />
      )}

      <div className="item pl-[2rem] flex items-center gap-8">
        {loading ? (
          <Skeleton
            circle
            className="rounded-full top-[-5rem] relative"
            height={"10rem"}
            width={"10rem"}
          />
        ) : (
          <Image
            height={400}
            width={400}
            className="block size-[10rem] object-cover bg-[rgba(var(--primary-color-rgb),0.7)] relative top-[-5rem] rounded-full shadow-lg"
            src={picture}
            alt=""
          />
        )}

        <div className="name-info -mt-4">
          {loading ? (
            <Skeleton height={40} width={200} />
          ) : (
            <h1 className="text-themeColor h3 !font-semibold">{name}</h1>
          )}

          {loading ? (
            <Skeleton width={150} />
          ) : (
            <h2 className="text-secondary-main h4 capitalize">{position}</h2>
          )}
        </div>
      </div>
    </div>
  );
};

interface BranchData {
  title: string;
  values: Record<string, number>;
}

// Example Usage
export const branchData: BranchData = {
  title: "Staff Summary",
  values: {
    total_staff: 100,
    property_for_rent: 100,
    property_for_sale: 100,
    property_for_short_let: 100,
    total_unit_managing: 100,
    total_reviews: 100,
    completed_transaction: 100,
  },
};

const SecondCol: React.FC<{ stats: StaffStats; loading: boolean }> = ({
  stats,
  loading,
}) => {

  return (
    <BranchSummaryCard
      loading={loading}
      title="About Staff"
      values={stats as unknown as Record<string, number>}
    />
  );
};

const FourthCol: React.FC<ExtendedStaffType> = ({
  name,
  staffListing,
  loading,
}) => {
  const isDemo = process.env.NEXT_PUBLIC_DEMO_MODE === "true";

  const options = [
    { value: "rent", label: "Rent" },
    { value: "short-let", label: "Shortlet" },
    { value: "sale", label: "Sale" },
  ];

  const [selectedValue, setSelectedValue] = useState<string | null>("rent");

  const handleSelect = (value: string) => {

    setSelectedValue(value);
  };

  const isFilteredOut = selectedValue && selectedValue !== "rent" && !isDemo;

  return (
    <div className="py-8">
      <div className="item mb-4 flex justify-between">
        <h2 className="text-primary h3">{name} Listing</h2>
        <div className="filter border h-max rounded-md border-themeColor p-1">
          <CustomSelect
            options={options}
            value={selectedValue}
            onChange={setSelectedValue}
            handleSelect={handleSelect}
            placeholder="All properties"
          />
        </div>
      </div>

      {isFilteredOut ? (
        <div>
          <NoSearchCriteria />
        </div>
      ) : loading ? (
        <CardGrid gap={15} maxColumns={4}>
          {Array.from({ length: 4 }).map((_, index) => (
            <PropertyCardSkeleton key={index} />
          ))}
        </CardGrid>
      ) : !loading && staffListing?.length === 0 ? (
        <div>No properties available</div>
      ) : (
        <CardGrid gap={15} maxColumns={4}>
          {staffListing?.map((item, index) => (
            <VariantCard key={index} {...item} />
          ))}
        </CardGrid>
      )}
    </div>
  );
};

export interface StaffProfile {
  coverImage: string;
  profileImage: string;
  name: string;
  title: string;
  description: string; // String of <p> tags
  phoneNumber: string;
  email: string;
  listings: PropertyType[];
  staffSummary: ProfileSummary;
}

export interface ListingType {
  id: number;
  title: string;
  price: number;
  location: string;
  image: string;
}

export interface StaffSummary {
  title: string;
  values: {
    property_for_rent: number;
    property_for_sale: number;
    property_for_short_let: number;
    total_unit_managing: number;
    years_in_business: string; // To accommodate "4 Years+"
    company_position: string;
  };
}

export const staffProfile: StaffProfile = {
  coverImage: "/images/person-3.webp",
  profileImage: "/images/person-3.webp",
  name: "Samson Tunde",
  title: "Estate Surveyor & Valuer",
  description: `
        <p>Samson Tunde is a highly skilled and seasoned real estate expert with more than a decade of experience in property management, residential sales, and real estate investment. Over the years, he has honed his expertise in navigating both the buying and selling sides of the market, providing a comprehensive understanding of every aspect of the real estate process.</p> <p>With a proven track record of successfully closing numerous high-value transactions, John has earned a reputation for his exceptional negotiation skills and ability to secure favorable outcomes for his clients. His extensive portfolio includes a diverse range of properties, from luxury estates to first-time homebuyer homes, demonstrating his versatility and commitment to meeting the unique needs of every client.</p> <p>Beyond his technical knowledge of the real estate market, John is known for his strong dedication to his clients and his client-first approach. He takes the time to understand the specific needs, desires, and financial situations of his clients, ensuring that every decision made aligns with their long-term goals. His in-depth market insight allows him to offer invaluable advice, helping clients make informed decisions in a constantly evolving market.</p> <p>John’s professionalism, integrity, and personalized service have garnered him a loyal client base, many of whom return for future transactions and refer him to friends and family. His commitment to excellence, paired with his ability to build lasting relationships, has made him a trusted name in the real estate industry.</p>`,
  phoneNumber: "+234 802 345 6789",
  email: "johndoe@example.com",
  listings: [
    ...mockProperties["forRent"],
    ...mockProperties["forSale"],
    ...mockProperties["forShortlet"],
  ],
  staffSummary: {
    title: "Staff Summary",
    values: {
      property_for_rent: 20,
      property_for_sale: 13,
      property_for_short_let: 8,
      total_unit_managing: 78,
      years_in_business: "4 Years+",
      company_position: "Estate Surveyor & Valuer",
    },
  },
};

const CallIcon = () => {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 20 20"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M6.66634 2.5C7.08301 2.5 8.74967 6.25 8.74967 6.66667C8.74967 7.5 7.49967 8.33333 7.08301 9.16667C6.66634 10 7.49967 10.8333 8.33301 11.6667C8.65801 11.9917 9.99967 13.3333 10.833 12.9167C11.6663 12.5 12.4997 11.25 13.333 11.25C13.7497 11.25 17.4997 12.9167 17.4997 13.3333C17.4997 15 16.2497 16.25 14.9997 16.6667C13.7497 17.0833 12.9163 17.0833 11.2497 16.6667C9.58301 16.25 8.33301 15.8333 6.24967 13.75C4.16634 11.6667 3.74967 10.4167 3.33301 8.75C2.91634 7.08333 2.91634 6.25 3.33301 5C3.74967 3.75 4.99967 2.5 6.66634 2.5Z"
        stroke="#2F364B"
        stroke-width="2"
        stroke-linecap="round"
        stroke-linejoin="round"
      />
    </svg>
  );
};

const MessageIcon = () => {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 20 20"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M5.83333 7.49996L8.95833 9.99996C9.25394 10.2366 9.62132 10.3656 10 10.3656C10.3787 10.3656 10.7461 10.2366 11.0417 9.99996L14.1667 7.49996M17.5 14.1666V5.83329C17.5 5.39127 17.3244 4.96734 17.0118 4.65478C16.6993 4.34222 16.2754 4.16663 15.8333 4.16663H4.16667C3.72464 4.16663 3.30072 4.34222 2.98816 4.65478C2.67559 4.96734 2.5 5.39127 2.5 5.83329V14.1666C2.5 14.6087 2.67559 15.0326 2.98816 15.3451C3.30072 15.6577 3.72464 15.8333 4.16667 15.8333H15.8333C16.2754 15.8333 16.6993 15.6577 17.0118 15.3451C17.3244 15.0326 17.5 14.6087 17.5 14.1666Z"
        stroke="#2F364B"
        stroke-width="2"
        stroke-linecap="round"
        stroke-linejoin="round"
      />
    </svg>
  );
};
